import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { BLOG_TOPICS } from "../../lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const EMPTY = { title: "", topic: "Decor Tips", excerpt: "", content: "", image_url: "" };

const inputCls =
  "w-full rounded-md border border-[#D8CDBC] bg-[#FDFBF7] px-3 py-2 text-sm text-[#292725] placeholder:text-[#8A7E70] focus:border-[#C27803] focus:outline-none focus:ring-2 focus:ring-[#C27803]/30";

export default function BlogTab({ onChanged }) {
  const [posts, setPosts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/blog-posts").then((r) => setPosts(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ title: p.title, topic: p.topic, excerpt: p.excerpt, content: p.content, image_url: p.image_url || "" });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/blog-posts/${editing.id}`, form);
        toast.success("Story updated");
      } else {
        await api.post("/blog-posts", form);
        toast.success("New story added to the Chronicles");
      }
      setOpen(false);
      load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Tear out "${p.title}" from the journal?`)) return;
    try {
      await api.delete(`/blog-posts/${p.id}`);
      toast.success("Story removed");
      load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-blog-tab">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-xl font-semibold text-[#292725]">{posts.length} stories</h2>
        <button
          onClick={openNew}
          data-testid="admin-add-post-button"
          className="flex items-center gap-2 rounded-md bg-[#C27803] px-4 py-2.5 text-sm font-semibold text-[#FDFBF7] transition-colors hover:bg-[#9A5E02]"
        >
          <Plus className="h-4 w-4" /> Write a Story
        </button>
      </div>

      <div className="mt-5 space-y-4">
        {posts.map((p) => (
          <div key={p.id} className="flex flex-wrap items-center gap-4 rounded-md border border-[#D8CDBC] bg-[#FDFBF7] p-4 shadow-sm" data-testid={`admin-post-row-${p.id}`}>
            <img src={p.image_url} alt="" className="h-14 w-20 rounded-sm border border-[#D8CDBC] object-cover" />
            <div className="min-w-0 flex-1">
              <p className="truncate font-serif text-lg font-semibold text-[#292725]">{p.title}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#C27803]">{p.topic}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => openEdit(p)} data-testid={`admin-edit-post-${p.id}`} className="rounded-md border border-[#D8CDBC] p-2 text-[#5C554B] transition-colors hover:border-[#C27803] hover:text-[#C27803]" aria-label="Edit">
                <Pencil className="h-4 w-4" />
              </button>
              <button onClick={() => remove(p)} data-testid={`admin-delete-post-${p.id}`} className="rounded-md border border-[#D8CDBC] p-2 text-[#5C554B] transition-colors hover:border-[#8C251C] hover:text-[#8C251C]" aria-label="Delete">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-[#D8CDBC] bg-[#FDFBF7] sm:max-w-xl" data-testid="post-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#292725]">{editing ? "Edit Story" : "Write a New Story"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A7E70]">Title *</label>
              <input data-testid="post-form-title" required value={form.title} onChange={set("title")} className={inputCls} />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A7E70]">Topic *</label>
                <select data-testid="post-form-topic" value={form.topic} onChange={set("topic")} className={inputCls}>
                  {BLOG_TOPICS.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A7E70]">Image URL</label>
                <input data-testid="post-form-image" value={form.image_url} onChange={set("image_url")} className={inputCls} placeholder="https://..." />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A7E70]">Excerpt *</label>
              <textarea data-testid="post-form-excerpt" required rows={2} value={form.excerpt} onChange={set("excerpt")} className={inputCls} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A7E70]">Story * (blank line between paragraphs)</label>
              <textarea data-testid="post-form-content" required rows={8} value={form.content} onChange={set("content")} className={inputCls} />
            </div>
            <button
              type="submit"
              data-testid="post-form-submit"
              disabled={saving}
              className="w-full rounded-md bg-[#C27803] py-3 text-sm font-semibold text-[#FDFBF7] transition-colors hover:bg-[#9A5E02] disabled:opacity-60"
            >
              {saving ? "Saving..." : editing ? "Save Changes" : "Press It Into the Journal"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
